"use client";

import { createContext, useContext, useMemo, useState } from "react";
import type { Map as MapprMap } from "@mappr/mapprbox-core";

interface MapContextValue {
  /** The live map, or null until it has been constructed. */
  map: MapprMap | null;
  /** True once the map has fired `load` and its style is usable. */
  isLoaded: boolean;
  /**
   * Increments every time a new style finishes loading.
   *
   * `setStyle` silently discards every source and layer that was added on top of
   * the basemap, and nothing in React knows it happened. Layer hooks depend on
   * this counter so a basemap switch re-runs them and they rebuild — which is
   * also why they must be written to be idempotent.
   */
  styleEpoch: number;
  setMap: (map: MapprMap | null) => void;
  setLoaded: (loaded: boolean) => void;
  bumpStyleEpoch: () => void;
}

const MapContext = createContext<MapContextValue | null>(null);

/**
 * Holds the map instance for everything that needs to talk to it.
 *
 * The legacy app kept this in a module-level `let mapInstance` and tried to make
 * components react to it with a `useEffect` keyed on the module binding — which
 * never changes identity, so consumers rendered once with `undefined` and were
 * never told the map had arrived. React state fixes that, and scoping it to a
 * provider also keeps two maps on one page from clobbering each other.
 *
 * `isLoaded` is tracked separately from `map` because they answer different
 * questions: the instance exists as soon as the constructor returns, but adding
 * sources or layers before `load` throws.
 */
export function MapProvider({ children }: { children: React.ReactNode }) {
  const [map, setMap] = useState<MapprMap | null>(null);
  const [isLoaded, setLoaded] = useState(false);
  const [styleEpoch, setStyleEpoch] = useState(0);

  const value = useMemo(
    () => ({
      map,
      isLoaded,
      styleEpoch,
      setMap,
      setLoaded,
      bumpStyleEpoch: () => setStyleEpoch((epoch) => epoch + 1),
    }),
    [map, isLoaded, styleEpoch]
  );

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
}

function useMapContext(): MapContextValue {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error("useMapInstance must be used inside <MapProvider>");
  }
  return context;
}

/** The live map instance, or null before it exists. */
export function useMapInstance(): MapprMap | null {
  return useMapContext().map;
}

/**
 * The map, but only once its style is ready.
 *
 * This is what layer code should use: it returns null until `load` has fired, so
 * an effect that adds sources or layers simply doesn't run early rather than
 * having to guard on `isStyleLoaded()` itself.
 */
export function useLoadedMap(): MapprMap | null {
  const { map, isLoaded } = useMapContext();
  return isLoaded ? map : null;
}

/**
 * The loaded map plus the current style generation.
 *
 * Layer hooks should use this and list `styleEpoch` in their deps, so switching
 * basemap re-adds what `setStyle` threw away.
 */
export function useStyledMap(): { map: MapprMap | null; styleEpoch: number } {
  const { map, isLoaded, styleEpoch } = useMapContext();
  return { map: isLoaded ? map : null, styleEpoch };
}

/** Internal: lets the map component publish what it created. */
export function useMapRegistration() {
  const { setMap, setLoaded, bumpStyleEpoch } = useMapContext();
  return { setMap, setLoaded, bumpStyleEpoch };
}
